// Co-op overlay + banner. In a co-op session everyone follows the same page (handled in CollabContext),
// so this draws the others in your room who are on the page you're on: their pointers, a ripple where they
// click, and an outline round the field they're editing (with their name), so two people don't type over
// each other. The banner says who you're with, lets you stop scrolling together for a while, and leave.
//
// A pointer is drawn on the element it's over on their screen (its anchor, see coopAnchor.ts), so it
// sits on the same row for you whatever your window size or scroll, and stays on it while you scroll.
// While you both scroll together, one view trails the other by a poll or so; while your view is moving, a
// pointer is drawn as far from the top-left of what shows of the grid as it is on its owner's screen (its
// frame), so a still pointer stays still instead of sliding with rows one of you hasn't scrolled to yet.
// Once your view settles it slides back onto its row: two screens needn't show the same top row when they
// stop (the end of a sheet in a shorter window, a phone beside a desktop).
// Between polls it GLIDES from the last spot to the new one (a rAF loop, no React re-render), which is
// what makes co-op feel live over plain HTTP polling. A pointer on something scrolled out of your view
// (the page's or a grid's own scroll, past its sticky header row or pinned column) waits at that edge
// with an arrow, side by side with any others there; clicking its name scrolls you there.
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { CoopAnchor } from '@technohull/shared';
import { IconCursor } from '../components/icons.js';
import { useCollab } from './CollabContext.js';
import { resolveAnchor } from './coopAnchor.js';
import { contentPoint, pageScroller, rangeOf, scrollerAround, stuckBetween, visibleBox, type Axis, type Box } from './coopScroll.js';

const GLIDE_MS = 380; // about one fast poll: a pointer arrives at its new spot as the next one lands
const RETRY_MS = 600; // an anchor that isn't on screen yet (rows still loading) is looked up again this often
const RIPPLE_MS = 650; // how long a click's ripple shows
const INSET_MS = 500; // a grid's sticky header row and pinned column are measured again this often
const SETTLE_MS = 1000; // my view counts as catching up with theirs until this long after it last moved
const SHIFT_OUT_MS = 350; // then a pointer slides back onto its row this gently
// views further apart than this aren't catching up with each other. A quick wheel leaves a follower three
// desktop screens behind for a moment, and a landscape phone (short, with taller rows) nine.
const FAR_SCREENS = 10;
const PILL_GAP = 6;
const PILL_H = 22;

interface Spot {
  anchor: CoopAnchor | null;
  frame: CoopAnchor | null; // the top-left of what showed of the grid it's in, on their screen
  x: number; // viewport fractions on their screen: the fallback
  y: number;
  el?: Element | null; // the anchor's element here, once found
  triedAt?: number;
  fel?: Element | null; // the frame's element here
  ftriedAt?: number;
  box?: Element | null; // the scroller it sits in, inside the page (undefined: not looked up yet)
}
interface Track {
  from: Spot;
  to: Spot;
  t0: number;
  w: Record<Axis, number>; // how much of the frame shift the pointer gets on each axis (0..1)
  wAt: number;
}
interface Ripple { node: HTMLDivElement; spot: Spot; t0: number }
type Off = '' | 'up' | 'down' | 'left' | 'right';

const sameSpot = (a: Spot, b: Spot) =>
  a.x === b.x && a.y === b.y && JSON.stringify(a.anchor) === JSON.stringify(b.anchor) && JSON.stringify(a.frame) === JSON.stringify(b.frame);
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** an anchor's element on this screen, looked up again now and then while it isn't (rows still loading). */
function lookUp(a: CoopAnchor | null, el: Element | null | undefined, triedAt: number | undefined, now: number): { el: Element | null; triedAt: number | undefined } {
  if (!a) return { el: null, triedAt };
  if (el && !el.isConnected) el = null;
  if (!el && (triedAt == null || now - triedAt > RETRY_MS)) return { el: resolveAnchor(a), triedAt: now };
  return { el: el ?? null, triedAt };
}
function elementOf(s: Spot, now: number): Element | null {
  const prev = s.el;
  const r = lookUp(s.anchor, s.el, s.triedAt, now);
  s.el = r.el;
  s.triedAt = r.triedAt;
  if (s.el !== prev) s.box = undefined;
  return s.el;
}
function frameElementOf(s: Spot, now: number): Element | null {
  const r = lookUp(s.frame, s.fel, s.ftriedAt, now);
  s.fel = r.el;
  s.ftriedAt = r.triedAt;
  return s.fel;
}

/** the nearest scroller around an element inside the page that scrolls here (a grid with its own
 *  scrollbars); null for the page itself. */
function gridAround(el: Element, main: Element): Element | null {
  for (let n = el.parentElement; n && n !== main; n = n.parentElement) if (rangeOf(n, 'y') || rangeOf(n, 'x')) return n;
  return null;
}

// how far a grid's sticky header row and pinned column reach into what shows of it (measured now and then)
const insets = new WeakMap<Element, { at: number; x: number; y: number }>();
function insetOf(el: Element, main: Element, now: number): { x: number; y: number } {
  const had = insets.get(el);
  if (had && now - had.at < INSET_MS) return had;
  const v = visibleBox(el, main);
  const pt = contentPoint(el, main);
  const next = pt ? { at: now, x: Math.max(0, pt.x - 1 - v.l), y: Math.max(0, pt.y - 1 - v.t) } : { at: now, x: had?.x ?? 0, y: had?.y ?? 0 };
  insets.set(el, next);
  return next;
}

/** where a spot's element can be seen: what shows of the grid it sits in (or the page), less the grid's
 *  sticky header row and pinned column (a row scrolled under those is out of view) unless it's in them
 *  itself. Null for something outside the page's content. */
function bandOf(s: Spot, el: Element, main: Element | null, page: Box, now: number): Box | null {
  if (!main?.contains(el)) return null;
  if (s.box === undefined || (s.box && !s.box.isConnected)) s.box = gridAround(el, main);
  if (!s.box) return page;
  const v = visibleBox(s.box, main);
  const inset = insetOf(s.box, main, now);
  const st = stuckBetween(el, s.box);
  return { l: Math.max(page.l, v.l + (st?.h ? 0 : inset.x)), t: Math.max(page.t, v.t + (st?.v ? 0 : inset.y)), r: Math.min(page.r, v.r), b: Math.min(page.b, v.b) };
}

/** the top-left of what shows of a scroller here, found the way a frame was on its sender's screen: past
 *  a grid's sticky header row and pinned column, or past the page's padding and any gap at its top (that
 *  moves as the page scrolls, so the page's is looked up afresh each frame). */
let pageCorner: { at: number; x: number; y: number } | null = null;
function cornerOf(k: Element, main: Element, now: number): { x: number; y: number } {
  const v = visibleBox(k, main);
  if (k === main) {
    if (pageCorner?.at !== now) {
      const pt = contentPoint(main, main);
      pageCorner = { at: now, x: pt?.x ?? v.l + 1, y: pt?.y ?? v.t + 1 };
    }
    return pageCorner;
  }
  const inset = insetOf(k, main, now);
  return { x: v.l + inset.x + 1, y: v.t + inset.y + 1 };
}

/** a pointer's frame shift on one axis: how far to move it from its row (in my px) to sit as far from the
 *  top-left of what shows of the grid as on its owner's screen, the scroller that's measured in, and
 *  whether my view could line up with theirs there at all. */
interface Shift { d: number; k: Element; lines: boolean }

/** The frame shift of a spot on each axis; none where they sent no frame (they look around on their own,
 *  or are typing) or it can't be worked out here. */
function frameShift(s: Spot, el: Element, main: Element, now: number): Record<Axis, Shift | null> {
  const out: Record<Axis, Shift | null> = { x: null, y: null };
  const f = s.frame ? frameElementOf(s, now) : null;
  if (!f || !s.frame) return out;
  const fr = f.getBoundingClientRect();
  if (!fr.width && !fr.height) return out;
  for (const axis of ['x', 'y'] as const) {
    const k = scrollerAround(f, axis, main);
    if (!k || !k.contains(el)) continue;
    const st = stuckBetween(el, k);
    if (axis === 'y' ? st?.v : st?.h) continue; // a header row or pinned column doesn't move as it scrolls
    const c = cornerOf(k, main, now);
    const v = visibleBox(k, main);
    const d = axis === 'y' ? c.y - (fr.top + s.frame.ay * fr.height) : c.x - (fr.left + s.frame.ax * fr.width);
    // views catching up are a scroll of mine apart, and near (see FAR_SCREENS). Past either end of my
    // scroll, or much further apart, they just differ (another window size at the end of a sheet, another
    // layout), and the pointer belongs on its row.
    const need = (axis === 'y' ? k.scrollTop : k.scrollLeft) - d;
    const lines = need >= -1 && need <= rangeOf(k, axis) + 1 && Math.abs(d) <= FAR_SCREENS * (axis === 'y' ? v.b - v.t : v.r - v.l);
    out[axis] = { d, k, lines };
  }
  return out;
}

// when each of my scrollers last moved along each axis (my own scrolling, or following the room's)
const moves = new WeakMap<Element, { top: number; left: number; y: number; x: number }>();
function noteScroll(el: Element, now: number) {
  const m = moves.get(el);
  if (!m) { moves.set(el, { top: el.scrollTop, left: el.scrollLeft, y: now, x: now }); return; }
  if (Math.abs(el.scrollTop - m.top) >= 1) { m.top = el.scrollTop; m.y = now; }
  if (Math.abs(el.scrollLeft - m.left) >= 1) { m.left = el.scrollLeft; m.x = now; }
}
function movedAt(el: Element, axis: Axis): number {
  let m = moves.get(el);
  if (!m) moves.set(el, (m = { top: el.scrollTop, left: el.scrollLeft, y: -Infinity, x: -Infinity }));
  return m[axis];
}

/** whether someone's pointer is over the app at all (not before it first moves, nor once it leaves). */
const pointing = (p: { anchor: CoopAnchor | null; x: number }) => !!p.anchor || p.x >= 0;

/** "Alice", "Alice and Bob", "Alice, Bob and Carol", "Alice, Bob +2" (first names). */
function withWhom(names: string[]): string {
  const first = names.map((n) => n.trim().split(/\s+/)[0] || n);
  if (first.length <= 2) return first.join(' and ');
  if (first.length === 3) return `${first[0]}, ${first[1]} and ${first[2]}`;
  return `${first[0]}, ${first[1]} +${first.length - 2}`;
}

export function CoopCursors() {
  const { coopOn, coopPeers, toggleCoop, coopScrollTogether, setCoopScrollTogether, coopViewHeld, coopViewPaused } = useCollab();
  const { pathname } = useLocation();
  // read by the rAF loop: whether my view moves with the room's right now, and whether a field I'm editing
  // keeps it from the room's (the banner says so)
  const withRoomNow = useRef<() => boolean>(() => false);
  withRoomNow.current = () => coopScrollTogether && !coopViewHeld();
  const pausedNow = useRef<() => boolean>(() => false);
  pausedNow.current = coopViewPaused;
  const [paused, setPaused] = useState(false);
  const pausedShown = useRef(false);

  const els = useRef(new Map<string, HTMLDivElement>()); // uid → cursor element (placed by the rAF loop)
  const fields = useRef(new Map<string, HTMLDivElement>()); // uid → the outline round their field
  const tracks = useRef(new Map<string, Track>()); // uid → the glide from the last spot to the newest
  const editing = useRef(new Map<string, Spot>()); // uid → the field they're in
  const clicks = useRef(new Map<string, number>()); // uid → their last click shown
  const ripples = useRef<Ripple[]>([]);
  const fx = useRef<HTMLDivElement>(null); // ripples are added in here

  // a fresh poll: start a glide to each moved pointer; a newly seen one starts AT its spot (no glide-in
  // from a corner), and pointers that left (or are on another page) are dropped. Fields and clicks too.
  useEffect(() => {
    const now = performance.now();
    const live = new Set<string>();
    for (const p of coopPeers) {
      if (p.path !== pathname) continue;
      live.add(p.uid);
      const to: Spot = { anchor: p.anchor ?? null, frame: p.frame ?? null, x: p.x, y: p.y };
      const cur = tracks.current.get(p.uid);
      if (!pointing(p)) tracks.current.delete(p.uid);
      else if (!cur) tracks.current.set(p.uid, { from: to, to, t0: now - GLIDE_MS, w: { x: 0, y: 0 }, wAt: now });
      else if (!sameSpot(cur.to, to)) tracks.current.set(p.uid, { ...cur, from: cur.to, to, t0: now });
      const f = editing.current.get(p.uid);
      if (!p.focus) editing.current.delete(p.uid);
      else if (!f || JSON.stringify(f.anchor) !== JSON.stringify(p.focus)) editing.current.set(p.uid, { anchor: p.focus, frame: null, x: 0, y: 0 });
      // a click not shown yet: a ripple on what they clicked (only where it's on this screen too)
      if (p.click && clicks.current.get(p.uid) !== p.click.n) {
        clicks.current.set(p.uid, p.click.n);
        const spot: Spot = { anchor: p.click.a, frame: null, x: 0, y: 0 };
        if (fx.current && elementOf(spot, now)) {
          const node = document.createElement('div');
          node.className = 'coop-ripple';
          node.style.color = p.color;
          fx.current.appendChild(node);
          ripples.current.push({ node, spot, t0: now });
        }
      }
    }
    for (const uid of [...tracks.current.keys()]) if (!live.has(uid)) tracks.current.delete(uid);
    for (const uid of [...editing.current.keys()]) if (!live.has(uid)) editing.current.delete(uid);
    for (const uid of [...clicks.current.keys()]) if (!coopPeers.some((p) => p.uid === uid)) clicks.current.delete(uid);
  }, [coopPeers, pathname]);

  // one rAF loop for everything drawn: work out all positions first, then write them (no layout thrash).
  useEffect(() => {
    if (!coopOn) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const onScroll = (e: Event) => { if (e.target instanceof Element) noteScroll(e.target, performance.now()); };
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    let raf = 0;
    const frame = () => {
      const now = performance.now();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // the visible band of the page's content: what scrolls above it is under the top bar
      const main = pageScroller();
      const page: Box = main ? visibleBox(main, main) : { l: 0, t: 0, r: vw, b: vh };
      // my view moves with the room's (so pointers can be drawn by their frames) unless I look around on my
      // own or it waits for a field I'm editing
      const withRoom = withRoomNow.current();

      /** where a spot is on this screen, in viewport px: on its row (x0, y0), and moved by `w` of its frame
       *  shift (x, y); and the band it can be seen in (null: anywhere, as for something outside the page's
       *  content, or the fallback). */
      const place = (s: Spot, w: Record<Axis, number>, shift?: Record<Axis, Shift | null> | null) => {
        const el = elementOf(s, now);
        if (el && s.anchor) {
          const r = el.getBoundingClientRect();
          if (r.width || r.height) {
            const x0 = r.left + s.anchor.ax * r.width;
            const y0 = r.top + s.anchor.ay * r.height;
            const sh = (w.x || w.y) && main && s.frame ? shift ?? frameShift(s, el, main, now) : null;
            return { x: x0 + (sh?.x?.d ?? 0) * w.x, y: y0 + (sh?.y?.d ?? 0) * w.y, x0, y0, band: bandOf(s, el, main, page, now) };
          }
        }
        return { x: s.x * vw, y: s.y * vh, x0: s.x * vw, y0: s.y * vh, band: null as Box | null };
      };
      const edge = (x: number, y: number, band: Box): Off => (y < band.t ? 'up' : y > band.b ? 'down' : x < band.l ? 'left' : x > band.r ? 'right' : '');

      const cursors: Array<{ el: HTMLDivElement; x: number; y: number; off: Off; w: number; lo: number; hi: number; flip: boolean }> = [];
      for (const [uid, el] of els.current) {
        const tr = tracks.current.get(uid);
        if (!tr) continue;
        // the frame shift applies at once while my view moves along an axis (my own scrolling, or my
        // following theirs), so the pointer keeps its place the very frame the rows jump; it eases off once
        // my view has settled, or where the views can't line up
        const at = elementOf(tr.to, now);
        const shift = withRoom && main && at && tr.to.frame ? frameShift(tr.to, at, main, now) : null;
        const dt = Math.min(100, Math.max(0, now - tr.wAt));
        tr.wAt = now;
        for (const axis of ['x', 'y'] as const) {
          const s = shift?.[axis];
          const want = !!s && s.lines && now - movedAt(s.k, axis) < SETTLE_MS;
          tr.w[axis] = want || reduced ? Number(want) : Math.max(0, tr.w[axis] - dt / SHIFT_OUT_MS);
        }
        const b = place(tr.to, tr.w, shift);
        const k = reduced ? 1 : Math.min(1, (now - tr.t0) / GLIDE_MS);
        let { x, y, x0, y0 } = b;
        if (k < 1) {
          const a = place(tr.from, tr.w);
          const e = 1 - (1 - k) ** 3; // ease-out
          x = a.x + (b.x - a.x) * e;
          y = a.y + (b.y - a.y) * e;
          x0 = a.x0 + (b.x0 - a.x0) * e;
          y0 = a.y0 + (b.y0 - a.y0) * e;
        }
        const band = b.band;
        let off: Off = band ? edge(x, y, band) : '';
        if (off && band && Math.max(tr.w.x, tr.w.y) < 1) {
          // my view has stopped and the shift is easing off: a pointer whose row is in view is drawn on it,
          // never left waiting at an edge. (While my view moves, rows pass through it as the views catch
          // up, so the pointer keeps to its owner's screen until then rather than flickering.)
          x = x0;
          y = y0;
          off = edge(x0, y0, band);
        }
        const w = (el.lastElementChild as HTMLElement | null)?.offsetWidth ?? 120;
        if (!off || !band) {
          // in view: the arrow where it points, its name turned to the left where it would run off the edge
          const cx = clamp(x, 4, vw - 24);
          cursors.push({ el, x: cx, y: clamp(y, 4, vh - 28), off, w, lo: 4, hi: vw - 4, flip: cx + 14 + w > vw - 4 });
          continue;
        }
        // out of view: a pill at that edge of the band (below a sticky header row, past a pinned column)
        const lo = Math.max(4, band.l + 4);
        const hi = Math.min(vw - 4, band.r - 4);
        if (off === 'up' || off === 'down') cursors.push({ el, x: clamp(x, lo, hi - w), y: off === 'up' ? band.t + 6 : band.b - 30, off, w, lo, hi, flip: false });
        else cursors.push({ el, x: off === 'left' ? lo : hi - w, y: clamp(y - PILL_H / 2, band.t + 4, band.b - PILL_H - 4), off, w, lo: band.t + 4, hi: band.b - 4, flip: false });
      }
      // pills on the same edge sit side by side (or one under another at the sides), never on top of each
      // other: laid out from the start, then pushed back from the far end so none hangs off the band
      const groups = new Map<string, typeof cursors>();
      for (const c of cursors) {
        if (!c.off) continue;
        const key = `${c.off}:${Math.round(c.off === 'up' || c.off === 'down' ? c.y : c.x)}`;
        groups.set(key, [...(groups.get(key) ?? []), c]);
      }
      for (const g of groups.values()) {
        const across = g[0]!.off === 'up' || g[0]!.off === 'down';
        const pos = (c: (typeof g)[number]) => (across ? c.x : c.y);
        const set = (c: (typeof g)[number], v: number) => { if (across) c.x = v; else c.y = v; };
        const size = (c: (typeof g)[number]) => (across ? c.w : PILL_H);
        g.sort((p, q) => pos(p) - pos(q));
        let next = -Infinity;
        for (const c of g) { set(c, Math.max(pos(c), next)); next = pos(c) + size(c) + PILL_GAP; }
        let limit = Math.min(...g.map((c) => c.hi));
        for (const c of [...g].reverse()) { set(c, Math.min(pos(c), limit - size(c))); limit = pos(c) - PILL_GAP; }
        const lo = Math.max(...g.map((c) => c.lo));
        for (const c of g) set(c, Math.max(pos(c), lo));
      }

      const fieldsOut: Array<[HTMLDivElement, Box | null, boolean]> = [];
      for (const [uid, el] of fields.current) {
        const s = editing.current.get(uid);
        const target = s ? elementOf(s, now) : null;
        if (!s || !target) { fieldsOut.push([el, null, false]); continue; }
        const r = target.getBoundingClientRect();
        // only what of the cell shows: not under my sticky header row or pinned column (and then the name
        // goes under it, off the header)
        const band = bandOf(s, target, main, page, now) ?? { l: 0, t: 0, r: vw, b: vh };
        const box = { l: Math.max(r.left, band.l), t: Math.max(r.top, band.t), r: Math.min(r.right, band.r), b: Math.min(r.bottom, band.b) };
        fieldsOut.push([el, box.r > box.l && box.b > box.t ? box : null, box.t > r.top + 0.5]);
      }
      const ripplesOut: Array<[HTMLDivElement, number, number, number, boolean]> = [];
      ripples.current = ripples.current.filter((rp) => {
        const age = now - rp.t0;
        const el = elementOf(rp.spot, now);
        if (age > RIPPLE_MS || !el || !rp.spot.anchor) { rp.node.remove(); return false; }
        const r = el.getBoundingClientRect();
        const x = r.left + rp.spot.anchor.ax * r.width;
        const y = r.top + rp.spot.anchor.ay * r.height;
        const band = bandOf(rp.spot, el, main, page, now);
        ripplesOut.push([rp.node, x, y, age / RIPPLE_MS, !band || (x >= band.l && x <= band.r && y >= band.t && y <= band.b)]);
        return true;
      });

      for (const { el, x, y, off, flip } of cursors) {
        el.style.transform = `translate(${x - 2}px, ${y - 2}px)`;
        if (!el.dataset.placed) el.dataset.placed = '1';
        if ((el.dataset.off ?? '') !== off) {
          if (off) el.dataset.off = off;
          else delete el.dataset.off;
        }
        if ((el.dataset.flip === '1') !== flip) {
          if (flip) el.dataset.flip = '1';
          else delete el.dataset.flip;
        }
      }
      for (const [el, box, under] of fieldsOut) {
        if (!box) { if (el.style.visibility !== 'hidden') el.style.visibility = 'hidden'; continue; }
        el.style.visibility = '';
        if ((el.dataset.under === '1') !== under) {
          if (under) el.dataset.under = '1';
          else delete el.dataset.under;
        }
        el.style.transform = `translate(${box.l}px, ${box.t}px)`;
        el.style.width = `${box.r - box.l}px`;
        el.style.height = `${box.b - box.t}px`;
      }
      for (const [node, x, y, k, shown] of ripplesOut) {
        node.style.transform = `translate(${x}px, ${y}px) scale(${reduced ? 1 : 0.35 + k * 0.9})`;
        node.style.opacity = shown ? String(1 - k) : '0';
      }
      const nowPaused = pausedNow.current();
      if (nowPaused !== pausedShown.current) {
        pausedShown.current = nowPaused;
        setPaused(nowPaused);
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, { capture: true });
      for (const rp of ripples.current) rp.node.remove();
      ripples.current = [];
    };
  }, [coopOn]);

  if (!coopOn) return null;
  const onPage = coopPeers.filter((p) => p.path === pathname); // only those on the page we're sharing
  const goTo = (uid: string) => {
    const el = tracks.current.get(uid)?.to.el;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    el?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <>
      <div className="coop-banner" role="status">
        <IconCursor size={15} />
        <span className="coop-who">
          {coopPeers.length ? <>Co-op with {withWhom(coopPeers.map((p) => p.name))}</> : <>Co-op &middot; waiting for others</>}
        </span>
        {coopPeers.length > 0 && (
          <button
            type="button"
            role="switch"
            aria-checked={coopScrollTogether}
            aria-label={paused ? 'Scroll together, paused while you edit' : 'Scroll together'}
            className="coop-scroll hit-44"
            data-paused={paused || undefined}
            title={paused
              ? 'The group scrolled on while you were editing. Leave the field, or scroll, to catch up with them.'
              : coopScrollTogether ? 'Your view scrolls with the group. Switch off to look around on your own for a while.' : 'Scroll with the group again'}
            onClick={() => setCoopScrollTogether(!coopScrollTogether)}
          >
            <span className="coop-switch" aria-hidden="true" />
            {/* the smallest phones keep "Scroll" (or "Paused"), so who you're with still fits */}
            {paused
              ? <span>Paused<span className="coop-scroll-more"> while you edit</span></span>
              : <span>Scroll<span className="coop-scroll-more"> together</span></span>}
          </button>
        )}
        <button className="coop-leave hit-44" onClick={toggleCoop}>Leave</button>
      </div>
      <div className="coop-layer" aria-hidden>
        <div ref={fx} />
        {onPage.filter((p) => p.focus).map((p) => (
          <div
            key={p.uid}
            ref={(el) => {
              if (el) fields.current.set(p.uid, el);
              else fields.current.delete(p.uid);
            }}
            className="coop-field"
            style={{ color: p.color, visibility: 'hidden' }}
          >
            <span className="coop-field-name" style={{ background: p.color }}>{p.name.trim().split(/\s+/)[0] || p.name}</span>
          </div>
        ))}
        {onPage.filter(pointing).map((p) => (
          <div
            key={p.uid}
            ref={(el) => {
              if (el) els.current.set(p.uid, el);
              else els.current.delete(p.uid);
            }}
            className="coop-cursor"
            style={{ color: p.color }}
          >
            <svg className="coop-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M2 2 L2 16 L6 12 L9 18 L11 17 L8 11 L14 11 Z" fill="currentColor" stroke="#fff" strokeWidth="1" />
            </svg>
            {/* off screen: the name points the way and takes you there */}
            <span className="coop-name" style={{ background: p.color }} onClick={() => goTo(p.uid)}>
              <span className="coop-dir" />
              {p.name}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}
